import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Checkbox,
  Avatar,
  Alert,
  Divider,
} from '@mui/material';
import BookingGuestFormDrawer, { resolveHotelCountryName } from './BookingGuestFormDrawer';
import {
  getForeignGuestChipSx,
  isForeignGuestRecord,
} from '../../utils/foreignGuestFormUtils';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import EditIcon from '@mui/icons-material/Edit';
import BedIcon from '@mui/icons-material/Bed';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import PersonIcon from '@mui/icons-material/Person';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import PublicIcon from '@mui/icons-material/Public';
import RoomIcon from '@mui/icons-material/Room';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import moment from 'moment';
import api from '../../services/api';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import { isGuestChild } from '../../utils/bookingUtils';
import { getBookingCurrencySymbol } from '../../utils/bookingPaymentSummary';
import {
  getBookingDrawerTheme,
  getBookingDrawerInputBg,
} from '../../utils/bookingDrawerLayout';
import { BookingDrawerCodeChip } from './DrawerEntityLink';

const CUSTOM_BORDER_COLOR = '#4ce1b6';

const getItemDateStr = (item) => {
  if (!item?.date) return '';
  return moment.isMoment(item.date)
    ? item.date.format('YYYY-MM-DD')
    : String(item.date).slice(0, 10);
};

const formatDateReadable = (dateStr) => {
  if (!dateStr) return '—';
  const m = moment(dateStr, 'YYYY-MM-DD');
  return m.isValid() ? m.format('ddd, D MMM YYYY') : dateStr;
};

const getAllocatedRoomId = (item) =>
  item?.allocated_room_id ?? item?.allocated_room?.id ?? null;

const getAllocatedRoomLabel = (item) => {
  const room = item?.allocated_room;
  return room?.number || room?.room_number || room?.name || item?.allocated_room_number || item?.room_number || 'No room';
};

const guestDisplayName = (guest) =>
  [guest?.first_name, guest?.middle_name, guest?.last_name].filter(Boolean).join(' ').trim()
  || guest?.name
  || 'Guest';


/**
 * Right-side drawer to edit booking items for a room group:
 * adults, children, daily rates, and guests.
 */
const EditBookingItemsDrawer = ({
  open,
  onClose,
  onSave,
  onGuestsChanged,
  group,
  booking,
  bookingId,
  roomTypes,
  ratePlans,
}) => {
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const hotelCountry = useMemo(() => resolveHotelCountryName(), []);
  const drawerTheme = getBookingDrawerTheme(isDark);
  const bookingCurrency = booking?.currency || 'INR';
  const bookingCurrencySymbol = getBookingCurrencySymbol(bookingCurrency);

  const sortedItems = useMemo(() => {
    const items = [...(group?.items || [])];
    return items.sort((a, b) => {
      const aDate = getItemDateStr(a);
      const bDate = getItemDateStr(b);
      return aDate.localeCompare(bDate) || (a?.id || 0) - (b?.id || 0);
    });
  }, [group?.items]);

  const [rateByItemId, setRateByItemId] = useState({});
  const [checkedInByItemId, setCheckedInByItemId] = useState({});
  const [checkoutByItemId, setCheckoutByItemId] = useState({});
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [localGuests, setLocalGuests] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [guestDrawerOpen, setGuestDrawerOpen] = useState(false);
  const [editingGuestId, setEditingGuestId] = useState(null);
  const [initialGuestForDrawer, setInitialGuestForDrawer] = useState(null);
  const [removingGuestId, setRemovingGuestId] = useState(null);

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => setCurrentTheme(newTheme));
    return cleanup;
  }, []);

  useEffect(() => {
    if (!open || !sortedItems.length) return;
    const initial = {};
    const initialCheckedIn = {};
    const initialCheckout = {};
    sortedItems.forEach((item) => {
      const id = item.id;
      if (id == null) return;
      const p = parseFloat(item.price);
      initial[id] = Number.isFinite(p) && p >= 0 ? String(p) : '';
      initialCheckedIn[id] = item.checked_in === true || item.checked_in === 1 || item.checked_in === 'true';
      initialCheckout[id] = item.checkout === true || item.checkout === 1 || item.checkout === 'true';
    });
    setRateByItemId(initial);
    setCheckedInByItemId(initialCheckedIn);
    setCheckoutByItemId(initialCheckout);

    const first = sortedItems[0];
    setAdults(Math.max(0, Number(first?.adults ?? 1) || 0));
    setChildren(Math.max(0, Number(first?.children ?? 0) || 0));

    const seen = new Set();
    const guests = [];
    sortedItems.forEach((it) => {
      (it.guests || []).forEach((g) => {
        const key = g?.id ?? guestDisplayName(g);
        if (key && !seen.has(key)) {
          seen.add(key);
          guests.push(g);
        }
      });
    });
    setLocalGuests(guests);
    setError(null);
    setGuestDrawerOpen(false);
    setEditingGuestId(null);
    setInitialGuestForDrawer(null);
  }, [open, sortedItems]);

  const firstItem = sortedItems[0];
  const roomTypeId = firstItem?.room_type_id ?? firstItem?.room_type?.id;
  const roomType = (roomTypes || []).find((rt) => Number(rt?.id) === Number(roomTypeId));
  const roomTypeName = roomType?.name
    || firstItem?.room_type_name
    || group?.room_type_display_name
    || group?.room_type_name
    || '—';
  const roomTypeImage =
    firstItem?.room_type?.image_url
    || firstItem?.room_type?.image
    || roomType?.image_url
    || roomType?.image
    || null;
  const ratePlanId = firstItem?.rate_plan_id ?? firstItem?.rate_plan?.id;
  const ratePlanName = (ratePlans || []).find((rp) => Number(rp?.id) === Number(ratePlanId))?.name
    || (ratePlans || []).find((rp) => Number(rp?.id) === Number(ratePlanId))?.code
    || firstItem?.rate_plan_name
    || null;

  const maxGuests = Math.max(
    1,
    Number(roomType?.max_guests ?? firstItem?.room_type?.max_guests ?? 0)
      || (
        (Number(roomType?.max_adults ?? firstItem?.room_type?.max_adults ?? 0) || 0)
        + (Number(roomType?.max_children ?? firstItem?.room_type?.max_children ?? 0) || 0)
      )
      || 6,
  );
  const maxAdults = Math.max(
    1,
    Number(roomType?.max_adults ?? firstItem?.room_type?.max_adults ?? maxGuests) || maxGuests,
  );
  const maxChildren = Math.max(
    0,
    Number(roomType?.max_children ?? firstItem?.room_type?.max_children ?? maxGuests) || maxGuests,
  );

  const nightCount = sortedItems.length;
  const totalFromRates = sortedItems.reduce((sum, item) => {
    const id = item.id;
    const raw = id != null ? rateByItemId[id] : '';
    const num = parseFloat(raw);
    const price = raw !== '' && Number.isFinite(num) && num >= 0 ? num : parseFloat(item.price) || 0;
    return sum + price;
  }, 0);

  const textPrimary = isDark ? drawerTheme.text : '#333';
  const textSecondary = isDark ? drawerTheme.subtleText : '#666';
  const targetItemId = sortedItems.find((it) => it?.id != null)?.id;

  const RATE_STEP = 10;

  const handleRateChange = useCallback((itemId, value) => {
    setRateByItemId((prev) => ({ ...prev, [itemId]: value }));
    setError(null);
  }, []);

  const handleRateStep = useCallback((itemId, direction) => {
    setRateByItemId((prev) => {
      const current = prev[itemId];
      const num = current === '' || current === undefined ? 0 : parseFloat(current);
      const valid = Number.isFinite(num) && num >= 0 ? num : 0;
      const next = direction === 'up' ? valid + RATE_STEP : Math.max(0, valid - RATE_STEP);
      return { ...prev, [itemId]: next % 1 === 0 ? String(next) : next.toFixed(2) };
    });
    setError(null);
  }, []);

  const handleCheckedInChange = useCallback((itemId, checked) => {
    setCheckedInByItemId((prev) => ({ ...prev, [itemId]: checked }));
    if (!checked) {
      setCheckoutByItemId((prev) => ({ ...prev, [itemId]: false }));
    }
    setError(null);
  }, []);

  const handleCheckoutChange = useCallback((itemId, checked) => {
    setCheckoutByItemId((prev) => ({ ...prev, [itemId]: checked }));
    if (checked) {
      setCheckedInByItemId((prev) => ({ ...prev, [itemId]: true }));
    }
    setError(null);
  }, []);

  const openAddGuest = () => {
    if (localGuests.length >= maxGuests) {
      setError(`This room allows up to ${maxGuests} guest(s).`);
      return;
    }
    setEditingGuestId(null);
    setInitialGuestForDrawer(null);
    setGuestDrawerOpen(true);
  };

  const openEditGuest = (guest) => {
    const fullName = [guest?.first_name, guest?.middle_name, guest?.last_name]
      .filter(Boolean).join(' ').trim() || guest?.name || '';
    const dobStr = guest?.dob
      ? (moment.isMoment(guest.dob) ? guest.dob.format('YYYY-MM-DD') : String(guest.dob).slice(0, 10))
      : '';
    setEditingGuestId(guest?.id ?? null);
    setInitialGuestForDrawer({
      title: guest?.title || '',
      name: fullName,
      first_name: guest?.first_name || '',
      middle_name: guest?.middle_name || '',
      last_name: guest?.last_name || '',
      email: guest?.email || '',
      phone: guest?.phone || '',
      phone_country_code: guest?.phone_country_code || guest?.country_code || '+91',
      dob: dobStr,
      gender: guest?.gender || '',
      address: guest?.address_line1 || '',
      city: guest?.city || '',
      state: guest?.state || '',
      country: guest?.country || '',
      zipcode: guest?.pincode || '',
      document_type: guest?.document_type || 'no_document',
      id_number: guest?.id_number || '',
      has_id_front: Boolean(guest?.has_id_front),
      has_id_back: Boolean(guest?.has_id_back),
    });
    setGuestDrawerOpen(true);
  };

  const handleRemoveGuest = async (guest) => {
    if (!bookingId || !guest?.id || !targetItemId) return;
    setRemovingGuestId(guest.id);
    setError(null);
    try {
      const itemIds = sortedItems.map((it) => it.id).filter(Boolean);
      for (const itemId of itemIds) {
        await api.patch(`/booking/api/${bookingId}/`, {
          booking_item_id: itemId,
          remove_guest_id: guest.id,
        });
      }
      if (onGuestsChanged) await onGuestsChanged();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to remove guest.');
    } finally {
      setRemovingGuestId(null);
    }
  };

  const handleUpdate = useCallback(() => {
    if (!onSave || !sortedItems.length) return;
    setError(null);
    const rateUpdates = [];
    for (const item of sortedItems) {
      const id = item.id;
      if (id == null) continue;
      const raw = rateByItemId[id];
      const num = parseFloat(raw);
      if (raw !== '' && (Number.isNaN(num) || num < 0)) {
        setError('All rates must be valid numbers ≥ 0.');
        return;
      }
      rateUpdates.push({
        id,
        date: item.date,
        price: Number.isFinite(num) && num >= 0 ? num : parseFloat(item.price) || 0,
        checked_in: Boolean(checkedInByItemId[id]),
        checkout: Boolean(checkoutByItemId[id]),
      });
    }
    setSubmitting(true);
    Promise.resolve(onSave({
      rateUpdates,
      adults: Math.max(0, Number(adults) || 0),
      children: Math.max(0, Number(children) || 0),
      itemIds: sortedItems.map((it) => it.id).filter((id) => id != null),
    }))
      .then(() => onClose())
      .catch((err) => setError(err?.message || 'Failed to update booking items.'))
      .finally(() => setSubmitting(false));
  }, [onSave, sortedItems, rateByItemId, checkedInByItemId, checkoutByItemId, adults, children, onClose]);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              borderBottom: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
              backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EditIcon sx={{ color: CUSTOM_BORDER_COLOR, fontSize: 24 }} />
              <Typography variant="h6" sx={{ color: textPrimary, fontWeight: 600 }}>
                Edit Booking Items
              </Typography>
              {booking?.code && (
                <BookingDrawerCodeChip
                  booking={booking}
                  bookingId={bookingId}
                  label={booking.code}
                  onNavigate={onClose}
                  sx={{
                    flexShrink: 0,
                  }}
                />
              )}
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ color: textSecondary, p: 0.5 }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                overflow: 'hidden',
                borderRadius: 3,
                mb: 2.5,
                minHeight: 160,
                boxShadow: isDark
                  ? '0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(76, 225, 182, 0.12)'
                  : '0 12px 40px rgba(76, 225, 182, 0.12), 0 4px 12px rgba(0,0,0, 0.06)',
                backgroundColor: isDark ? '#1a1a20' : '#fff',
                border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.25)'}`,
              }}
            >
              <Box
                sx={{
                  width: 140,
                  minWidth: 140,
                  minHeight: 160,
                  flexShrink: 0,
                  backgroundImage: roomTypeImage ? `url(${roomTypeImage})` : 'none',
                  backgroundColor: isDark ? '#2a2a32' : '#eaeaea',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {!roomTypeImage && (
                  <RoomIcon sx={{ fontSize: 40, color: isDark ? '#555' : '#999', opacity: 0.5 }} />
                )}
              </Box>
              <Box sx={{ flex: 1, p: 2, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <BedIcon sx={{ color: CUSTOM_BORDER_COLOR, fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: textPrimary }}>
                    {roomTypeName}
                  </Typography>
                </Box>
                {ratePlanName && (
                  <Typography variant="body2" sx={{ color: textSecondary, fontStyle: 'italic', mb: 1 }}>
                    {ratePlanName}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <NightsStayIcon sx={{ color: textSecondary, fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: textSecondary }}>
                      {nightCount} night{nightCount !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    icon={(
                      <Typography
                        component="span"
                        sx={{ fontSize: '0.85rem !important', fontWeight: 900 }}
                      >
                        {bookingCurrencySymbol}
                      </Typography>
                    )}
                    label={totalFromRates.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    sx={{
                      backgroundColor: CUSTOM_BORDER_COLOR,
                      color: '#000',
                      fontWeight: 700,
                      '& .MuiChip-icon': { color: '#000' },
                    }}
                  />
                </Box>
              </Box>
            </Paper>

            {/* Adults / Children */}
            <Typography variant="subtitle2" sx={{ color: textPrimary, fontWeight: 600, mb: 1.25 }}>
              Occupancy
            </Typography>
            <Paper
              elevation={0}
              sx={{
                border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                borderRadius: 2,
                p: 2,
                mb: 2.5,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <PersonIcon sx={{ color: CUSTOM_BORDER_COLOR, fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: textPrimary }}>
                    Adults (Max: {maxAdults})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => setAdults((prev) => Math.max(0, Number(prev || 0) - 1))}
                    disabled={adults <= 0}
                    sx={{
                      border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                      '&:hover': { backgroundColor: `${CUSTOM_BORDER_COLOR}20` },
                    }}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="h6" sx={{ minWidth: 36, textAlign: 'center', color: textPrimary, fontWeight: 600 }}>
                    {adults}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setAdults((prev) => Math.min(maxAdults, Number(prev || 0) + 1))}
                    disabled={adults >= maxAdults}
                    sx={{
                      border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                      '&:hover': { backgroundColor: `${CUSTOM_BORDER_COLOR}20` },
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <ChildCareIcon sx={{ color: CUSTOM_BORDER_COLOR, fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: textPrimary }}>
                    Children (Max: {maxChildren})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => setChildren((prev) => Math.max(0, Number(prev || 0) - 1))}
                    disabled={children <= 0}
                    sx={{
                      border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                      '&:hover': { backgroundColor: `${CUSTOM_BORDER_COLOR}20` },
                    }}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="h6" sx={{ minWidth: 36, textAlign: 'center', color: textPrimary, fontWeight: 600 }}>
                    {children}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setChildren((prev) => Math.min(maxChildren, Number(prev || 0) + 1))}
                    disabled={children >= maxChildren}
                    sx={{
                      border: `1px solid ${CUSTOM_BORDER_COLOR}`,
                      '&:hover': { backgroundColor: `${CUSTOM_BORDER_COLOR}20` },
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Paper>

            {/* Guests */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
              <Typography variant="subtitle2" sx={{ color: textPrimary, fontWeight: 600 }}>
                Guests
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={openAddGuest}
                disabled={localGuests.length >= maxGuests || !targetItemId}
                sx={{
                  color: CUSTOM_BORDER_COLOR,
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                Add guest
              </Button>
            </Box>
            <Paper
              elevation={0}
              sx={{
                border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                borderRadius: 2,
                p: localGuests.length ? 0 : 2,
                mb: 2.5,
              }}
            >
              {localGuests.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 1 }}>
                  <PeopleAltIcon sx={{ color: textSecondary, fontSize: 32, mb: 0.5, opacity: 0.6 }} />
                  <Typography variant="body2" sx={{ color: textSecondary }}>
                    No guests assigned to this room yet.
                  </Typography>
                </Box>
              ) : (
                localGuests.map((guest, idx) => {
                  const name = guestDisplayName(guest);
                  const child = isGuestChild(guest);
                  return (
                    <Box key={guest?.id ?? `${name}-${idx}`}>
                      {idx > 0 && <Divider sx={{ borderColor: isDark ? '#333' : '#eee' }} />}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                          px: 1.5,
                          py: 1.25,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: child
                              ? (isDark ? 'rgba(255, 167, 38, 0.2)' : 'rgba(255, 152, 0, 0.15)')
                              : (isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.18)'),
                            color: child ? (isDark ? '#ffb74d' : '#ef6c00') : CUSTOM_BORDER_COLOR,
                            fontSize: '0.85rem',
                            fontWeight: 700,
                          }}
                        >
                          {(name || 'G').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 600 }}>
                              {name}
                            </Typography>
                            {child && (
                              <Chip
                                size="small"
                                label="Child"
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  backgroundColor: isDark ? 'rgba(255, 167, 38, 0.18)' : 'rgba(255, 152, 0, 0.12)',
                                  color: isDark ? '#ffb74d' : '#ef6c00',
                                }}
                              />
                            )}
                            {isForeignGuestRecord(guest, hotelCountry) && (
                              <Chip
                                size="small"
                                icon={<PublicIcon sx={{ fontSize: '12px !important' }} />}
                                label="Foreign"
                                sx={getForeignGuestChipSx(isDark, { height: 20, fontSize: '0.65rem' })}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" sx={{ color: textSecondary }}>
                            {[guest?.gender, guest?.phone].filter(Boolean).join(' · ') || '—'}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => openEditGuest(guest)}
                          sx={{ color: CUSTOM_BORDER_COLOR }}
                          aria-label="Edit guest"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveGuest(guest)}
                          disabled={!guest?.id || removingGuestId === guest.id}
                          sx={{ color: isDark ? '#ef9a9a' : '#c62828' }}
                          aria-label="Remove guest"
                        >
                          {removingGuestId === guest.id
                            ? <CircularProgress size={16} color="inherit" />
                            : <DeleteOutlineIcon fontSize="small" />}
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Paper>

            {/* Daily rates */}
            <Typography variant="subtitle2" sx={{ color: textPrimary, fontWeight: 600, mb: 1.25 }}>
              Daily rates
            </Typography>
            <Paper
              elevation={0}
              sx={{
                border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                borderRadius: 2,
                overflow: 'visible',
                mb: 1,
              }}
            >
              <Table
                size="small"
                sx={{
                  tableLayout: 'fixed',
                  width: '100%',
                  '& .MuiTableCell-root': {
                    px: { xs: 0.75, sm: 1.25 },
                    py: 0.75,
                  },
                }}
              >
                <TableHead>
                  <TableRow sx={{ backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)' }}>
                    <TableCell sx={{ color: textSecondary, fontWeight: 600, width: '30%' }}>Date</TableCell>
                    <TableCell sx={{ color: textSecondary, fontWeight: 600, width: '25%' }}>
                      Rate ({bookingCurrencySymbol})
                    </TableCell>
                    <TableCell align="center" sx={{ color: textSecondary, fontWeight: 600, whiteSpace: 'nowrap', width: '17%' }}>
                      Checked in
                    </TableCell>
                    <TableCell align="center" sx={{ color: textSecondary, fontWeight: 600, whiteSpace: 'nowrap', width: '13%' }}>
                      Room
                    </TableCell>
                    <TableCell align="center" sx={{ color: textSecondary, fontWeight: 600, whiteSpace: 'nowrap', width: '15%' }}>
                      Checkout
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedItems.map((item, index) => {
                    const id = item.id;
                    const dateStr = getItemDateStr(item);
                    const value = id != null ? (rateByItemId[id] ?? '') : '';
                    const roomId = getAllocatedRoomId(item);
                    const roomLabel = getAllocatedRoomLabel(item);
                    const previousStreamItem = [...sortedItems]
                      .slice(0, index)
                      .reverse()
                      .find((candidate) =>
                        Number(candidate?.room_type_id ?? candidate?.room_type?.id) === Number(item?.room_type_id ?? item?.room_type?.id)
                        && Number(candidate?.room_sequence || 1) === Number(item?.room_sequence || 1)
                      );
                    const previousRoomId = getAllocatedRoomId(previousStreamItem);
                    const roomChanged = Boolean(
                      item?.room_changed_at
                      || (
                        previousRoomId
                        && roomId
                        && Number(previousRoomId) !== Number(roomId)
                      )
                    );
                    return (
                      <TableRow
                        key={id ?? dateStr}
                        sx={{
                          '&:last-child td': { borderBottom: 0 },
                          '&:hover': {
                            backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(76, 225, 182, 0.04)',
                          },
                        }}
                      >
                        <TableCell sx={{ color: textPrimary, fontWeight: 600, whiteSpace: 'nowrap', fontSize: '0.92rem' }}>
                          {formatDateReadable(dateStr)}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'middle' }}>
                          <TextField
                            variant="outlined"
                            size="small"
                            type="number"
                            placeholder={`Rate (${bookingCurrencySymbol})`}
                            value={value}
                            onChange={(e) => handleRateChange(id, e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end" sx={{ mr: -0.5 }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 0 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRateStep(id, 'up')}
                                      sx={{ p: 0, height: 17, color: textSecondary, '&:hover': { color: CUSTOM_BORDER_COLOR } }}
                                      aria-label="Increase rate"
                                    >
                                      <KeyboardArrowUp sx={{ fontSize: 16 }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRateStep(id, 'down')}
                                      sx={{ p: 0, height: 17, color: textSecondary, '&:hover': { color: CUSTOM_BORDER_COLOR } }}
                                      aria-label="Decrease rate"
                                    >
                                      <KeyboardArrowDown sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Box>
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              width: '100%',
                              maxWidth: '100%',
                              '& .MuiOutlinedInput-root': {
                                height: 40,
                                pr: 0.5,
                                borderRadius: 1.5,
                                backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
                              },
                              '& .MuiInputBase-input': { px: 1.25 },
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? '#555' : '#e0e0e0' },
                              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: CUSTOM_BORDER_COLOR },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1.5, borderColor: CUSTOM_BORDER_COLOR },
                              '& input[type=number]': { MozAppearance: 'textfield' },
                              '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                              '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                          <Checkbox
                            size="small"
                            checked={Boolean(checkedInByItemId[id])}
                            onChange={(e) => handleCheckedInChange(id, e.target.checked)}
                            sx={{
                              color: isDark ? drawerTheme.subtleText : '#9e9e9e',
                              '&.Mui-checked': { color: CUSTOM_BORDER_COLOR },
                            }}
                            inputProps={{ 'aria-label': `Checked in for ${formatDateReadable(dateStr)}` }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                          <Chip
                            size="small"
                            icon={roomChanged ? <SwapHorizIcon sx={{ fontSize: 14 }} /> : undefined}
                            label={roomLabel}
                            variant={roomId ? 'filled' : 'outlined'}
                            sx={{
                              height: 24,
                              maxWidth: 96,
                              fontWeight: 700,
                              color: roomId ? '#064e3b' : textSecondary,
                              backgroundColor: roomId
                                ? (roomChanged ? 'rgba(251, 146, 60, 0.22)' : 'rgba(76, 225, 182, 0.2)')
                                : 'transparent',
                              border: roomChanged
                                ? '1px solid rgba(251, 146, 60, 0.6)'
                                : `1px solid ${roomId ? 'rgba(76, 225, 182, 0.45)' : (isDark ? '#555' : '#ddd')}`,
                              '& .MuiChip-label': {
                                px: 0.75,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                              '& .MuiChip-icon': {
                                color: roomChanged ? '#f97316' : CUSTOM_BORDER_COLOR,
                                ml: 0.5,
                                mr: -0.25,
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                          <Checkbox
                            size="small"
                            checked={Boolean(checkoutByItemId[id])}
                            onChange={(e) => handleCheckoutChange(id, e.target.checked)}
                            sx={{
                              color: isDark ? drawerTheme.subtleText : '#9e9e9e',
                              '&.Mui-checked': { color: CUSTOM_BORDER_COLOR },
                            }}
                            inputProps={{ 'aria-label': `Checkout for ${formatDateReadable(dateStr)}` }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderTop: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
              backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
              display: 'flex',
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={onClose}
              disabled={submitting}
              sx={{
                borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                color: isDark ? drawerTheme.text : '#666',
                '&:hover': {
                  borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                  backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleUpdate}
              disabled={submitting || sortedItems.length === 0}
              sx={{
                backgroundColor: CUSTOM_BORDER_COLOR,
                color: '#000',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#3ab896' },
                '&:disabled': {
                  backgroundColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                  color: isDark ? drawerTheme.subtleText : '#666',
                },
              }}
            >
              {submitting ? <CircularProgress size={20} color="inherit" /> : 'Update'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      <BookingGuestFormDrawer
        open={guestDrawerOpen}
        onClose={() => setGuestDrawerOpen(false)}
        bookingId={bookingId}
        booking={booking}
        roomLabel={roomTypeName !== '—' ? roomTypeName : undefined}
        editingGuestId={editingGuestId}
        initialGuest={initialGuestForDrawer}
        bookingItemIds={sortedItems.map((it) => it.id).filter(Boolean)}
        onSaved={async () => {
          setGuestDrawerOpen(false);
          if (onGuestsChanged) await onGuestsChanged();
        }}
      />
    </>
  );
};

export default EditBookingItemsDrawer;
